import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import { BASE_ATTRIBUTE_NAMES } from '@verantos-dev/core-annotation-tool';
import type { Annotation, AttributeValue } from '@verantos-dev/core-annotation-tool';

import { useGetAnnotationWorkspaceQuery } from '../../api/annotationApiSlice';
import {
  clearAddEvidenceModeForConceptId,
  clearCreateAnnotationError,
  clearHighlightedTextSpans,
  clearPendingSpansForAnnotation,
} from '../../state/annotationSlice';
import { selectConceptAnnotationIdsInEditMode } from '../../state/selector';

interface UseAnnotationConceptsProps {
  conceptId: string;
}

interface UseAnnotationConceptsResults {
  savedAnnotationsCount: number;
  newAnnotationCardIds: string[];
  handleCreateAnnotation: () => void;
  shouldShowCreateButton: boolean;
  annotations: Annotation[];
  annotationsLoading: boolean;
  removeNewAnnotationCardId: (tempId: string) => void;
}

export const useAnnotationConcepts = ({
  conceptId,
}: UseAnnotationConceptsProps): UseAnnotationConceptsResults => {
  const { taskId } = useParams();

  const {
    data: annotationWorkspaceData,
    isLoading: isAnnotationWorkspaceLoading,
    isFetching: isAnnotationWorkspaceFetching,
  } = useGetAnnotationWorkspaceQuery({ taskId: taskId as string });
  const dispatch = useAppDispatch();
  const conceptAnnotationIdsInEditMode = useAppSelector(selectConceptAnnotationIdsInEditMode);

  const annotations = annotationWorkspaceData?.data.annotations.filter(
    (annotation) => annotation.conceptId === conceptId
  );

  // Track new annotation cards being created (temporary IDs)
  const [newAnnotationCardIds, setNewAnnotationCardIds] = useState<string[]>([]);

  const removeNewAnnotationCardId = (tempId: string) => {
    dispatch(clearAddEvidenceModeForConceptId());
    dispatch(clearPendingSpansForAnnotation(tempId));
    dispatch(clearHighlightedTextSpans());
    dispatch(clearCreateAnnotationError());
    setNewAnnotationCardIds((prev) => prev.filter((id) => id !== tempId));
  };

  // Check if the button should be displayed:
  // 1. Concept has at least one saved annotation
  // 2. That saved annotation has attributes other than "experienced", "present", and "current"
  const shouldShowCreateButton = useMemo(() => {
    if (!annotations || annotations.length === 0) {
      return false;
    }

    const hasUnsavedAnnotationCard = newAnnotationCardIds.length > 0;
    const hasAnnotationInEditMode = conceptAnnotationIdsInEditMode.some((id) =>
      id.startsWith(conceptId)
    );

    if (hasUnsavedAnnotationCard || hasAnnotationInEditMode) {
      return false;
    }

    // Check if any saved annotation has attributes other than the excluded ones
    const excludedAttributeNames = BASE_ATTRIBUTE_NAMES;
    return annotations.some((annotation) => {
      if (!annotation.attributes || annotation.attributes.length === 0) {
        return false;
      }

      const isValidAttribute = (attribute: AttributeValue) => {
        return (attribute.value !== undefined && attribute.value !== null) || attribute.unknown;
      };
      // Check if annotation has any attribute that is not in the excluded list
      return annotation.attributes.some(
        (attribute) =>
          !excludedAttributeNames.includes(attribute.name) && isValidAttribute(attribute)
      );
    });
  }, [annotations, conceptAnnotationIdsInEditMode, conceptId, newAnnotationCardIds.length]);

  const handleCreateAnnotation = () => {
    // Generate a unique temporary ID for the new annotation card
    const tempId = `new-${conceptId}-${Date.now()}`;
    setNewAnnotationCardIds((prev) => [...prev, tempId]);
  };

  // Calculate annotation numbers
  const savedAnnotationsCount = annotations?.length || 0;

  return {
    savedAnnotationsCount,
    newAnnotationCardIds,
    handleCreateAnnotation,
    shouldShowCreateButton,
    annotations: annotations || [],
    annotationsLoading: isAnnotationWorkspaceLoading || isAnnotationWorkspaceFetching,
    removeNewAnnotationCardId,
  };
};
